import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import OfferTabV3 from 'Layout/OfferTabV3'
import Collapsible from 'Components/Collapsible'
import { isNilEmpty } from 'Utils'
import SvgIcon from 'Components/SvgIcon'
import { intervalSale, intervalSqft } from 'Defaults/inputData'
// import { formatTimestamp } from 'Utils/buildingUtils'
import ShowOnVisible from 'Components/ShowOnVisible'
import ToolTipV2 from 'Components/ToolTipV2'
import PreconList from './components/PreconList'
import { _PreconFloorPlanContainer, _TopBar, _LastUpdated, _NoListings, _FavCTA, _FilterSortBar, _TabContainer } from './styles'
import FilterBar from './components/FilterBar'
import SortBar from './components/SortBar'


const disclaimer = `Our database is managed by a third-party team in collaboration with a network of real estate
developers and brokers. While we strive for accuracy, the content on our site is for reference only, and we are not liable
for its use or misuse. Prices, sizes, specifications, and promotions are subject to change by the builder without notice.
Please contact one of our agents for the latest information.`

export const maxPriceValue = 999999999
export const maxSqftValue = 10000

const PreconFloorPlan = ({
  googleAnalyticsEvent = () => { },
  buildingName = '',
  collapsibleIdSuffix,
  building,
  favourited,
  favBuilding = () => { },
  pageFrom,
}) => {
  // const lastUpdate = formatTimestamp(building?.source_timestamp)
  const offerTypeConst = {
    sale: 'Sale',
    sold: 'Sold',
  }

  const [listingsTab, setListingsTab] = useState(offerTypeConst.sale)
  const [allListings, setAllListings] = useState([])
  const [filteredListings, setFilteredListings] = useState([])

  const noData = !((building?.listings?.precon_for_sale)?.length) && !((building?.listings?.precon_sold)?.length)

  const forSaleUnits = building?.listings?.precon_for_sale || []
  const soldOutUnits = building?.listings?.precon_sold || []
  const saleStatus = building?.sales_status
  const constructionStatus = building?.construction_status

  const getMessage = () => {
    switch(true) {
      case noData && (saleStatus === 'Register Now' || saleStatus === 'Coming Soon' || saleStatus === 'Selling Now'):
        return {
          title: 'This project will be launching soon. Stay tuned!',
          message: 'Save this project to your favourites to know when new units become available for sale.',
          cta: 'Favourite',
        }
      case !forSaleUnits?.length > 0 && soldOutUnits?.length > 0:
        return {
          title: 'There are currently no available units for sale.',
          // message: 'Get alerted when more units become available.',
          message: 'Favourite this project to keep an eye on it',
          cta: 'Favourite',
        }
      case forSaleUnits?.length > 0 && !soldOutUnits?.length > 0:
        return {
          title: 'This project currently doesn’t have any sold out units.',
          cta: null,
        }
      case noData && constructionStatus === 'Complete':
        return {
          title: 'There are currently no available units for sale.',
          // message: 'Get alerted when more units become available.',
          message: 'Favourite this project to keep an eye on it',
          cta: 'Favourite',
        }
      case !noData && filteredListings?.length === 0:
        return {
          title: 'There are no units that match the filter criteria.',
        }
      default:
        return null
    }
  }

  const messageData = getMessage()

  const tabs = {
    Sale: `For Sale (${(building?.listings?.precon_for_sale)?.length || 0})`,
    Sold: `Sold Out (${(building?.listings?.precon_sold)?.length || 0})`,
  }

  const onOfferChange = (offer) => {
    setListingsTab(offer)
  }

  // SORT
  const sortType = {
    priceLowToHigh: 'Price Low to High',
    priceHighToLow: 'Price High to Low',
    sizeLowToHigh: 'Size Low to High',
    sizeHighToLow: 'Size High to Low',
  }
  const sortTypeMap = [
    {
      label: sortType.priceLowToHigh,
      value: 'priceLowToHigh',
    },
    {
      label: sortType.priceHighToLow,
      value: 'priceHighToLow',
    },
    {
      label: sortType.sizeLowToHigh,
      value: 'sizeLowToHigh',
    },
    {
      label: sortType.sizeHighToLow,
      value: 'sizeHighToLow',
    },
  ]
  const [sortTypeState, setSortTypeState] = useState(sortType.priceLowToHigh)


  useEffect(() => {
    // const forSaleUnits = building?.listings?.precon_for_sale || []
    // const soldOutUnits = building?.listings?.precon_sold || []

    setAllListings(listingsTab === offerTypeConst.sale ? forSaleUnits : soldOutUnits)
  }, [listingsTab, building])

  useEffect(() => {
    setFilteredListings((prev) =>
      [...prev].sort((a, b) => {
        switch(sortTypeState) {
          case sortType.priceLowToHigh:
            return a.asking_price - b.asking_price
          case sortType.priceHighToLow:
            return b.asking_price - a.asking_price
          case sortType.sizeLowToHigh:
            return a.sqft - b.sqft
          case sortType.sizeHighToLow:
            return b.sqft - a.sqft
          default:
            return 0
        }
      }),
    )
  }, [sortTypeState])

  // FILTER
  const [priceFilter, setPriceFilter] = useState(null)
  const [priceMetaData, setPriceMetaData] = useState({
    prefix: '$',
    intervals: intervalSale,
    value: [null, null],
  })
  const [sqftFilter, setSqftFilter] = useState(null)
  const [sqftMetaData, setSqftMetaData] = useState({
    prefix: '',
    suffix: 'ft\u00B2',
    intervals: intervalSqft,
    value: [null, null],
  })

  const [bedFilter, setBedFilter] = useState([])


  const filterHandler = (priceString, sqftString, bedArray) => {
    const [minPrice, maxPrice] = priceString?.split(',')?.map(Number) || [null, null]
    const [minSqft, maxSqft] = sqftString?.split(',')?.map(Number) || [null, null]
    setFilteredListings(() =>
      allListings.filter(
        (item) => {
          if(minPrice && !(item.asking_price >= minPrice)) return false
          if(maxPrice && !(item.asking_price <= maxPrice)) return false
          if(minSqft && !(item.sqft >= minSqft)) return false
          if(maxSqft && !(item.sqft <= maxSqft)) return false
          if(bedArray?.length === 0) return true
          if(!bedArray?.includes(item?.bed_type)) return false
          return true
        },
      ),
    )
  }

  useEffect(() => {
    filterHandler(priceFilter, sqftFilter, bedFilter)
  }, [priceFilter, sqftFilter, bedFilter])

  const priceChangeHandler = (e) => {
    const tempValue = e
    if((tempValue[0] === null || tempValue[0] === 0) && (tempValue[1] === null || tempValue[1] === maxPriceValue)) {
      setPriceFilter(null)
      setPriceMetaData((prev) => ({
        ...prev,
        value: [...e],
      }))
      return null
    }
    if(tempValue[1] === null) tempValue[1] = maxPriceValue
    const parsedValue = tempValue.join(',')
    setPriceFilter(parsedValue)
    setPriceMetaData((prev) => ({
      ...prev,
      value: [...e],
    }))
  }

  const sqftChangeHandler = (e) => {
    const tempValue = e
    if((tempValue[0] === null || tempValue[0] === 0) && (tempValue[1] === null || tempValue[1] === maxSqftValue)) {
      setSqftFilter(null)
      setSqftMetaData((prev) => ({
        ...prev,
        value: [...e],
      }))
      return null
    }
    if(tempValue[1] === null) tempValue[1] = maxSqftValue
    const parsedValue = tempValue.join(',')
    setSqftFilter(parsedValue)
    setSqftMetaData((prev) => ({
      ...prev,
      value: [...e],
    }))
  }

  const bedFilterChangeHandler = (bed) => {
    setBedFilter((prev) => {
      if(prev.includes(bed)) {
        return [...prev.filter((filter) => filter !== bed)]
      }
      return [...prev, bed]
    })
  }

  useEffect(() => {
    setPriceFilter(null)
    setSqftFilter(null)
    setBedFilter([])
    setPriceMetaData((prev) => ({
      ...prev,
      value: [null, null],
    }))
    setSqftMetaData((prev) => ({
      ...prev,
      value: [null, null],
    }))
  }, [listingsTab])

  useEffect(() => {
    setFilteredListings([...allListings])
  }, [allListings])


  // Sort possible bed options
  const bedTypeList = [...new Set(allListings.map((listing) => listing.bed_type).sort((a, b) => {
    if(!isNaN(a) && !isNaN(b)) {
      return a - b
    }
    return a.localeCompare(b)
  }))]

  return (
    <Collapsible
      title={`${buildingName || '-'} Pre-Construction Listings`}
      inputId={'Building floorplans' + collapsibleIdSuffix}
      seoText={'Learn about units available at ' + buildingName}
      showSeparatorLine
      defaultShowState
    >
      <ShowOnVisible>
        <_PreconFloorPlanContainer listings={filteredListings?.length > 0}>
          <_TopBar>
            <_TabContainer>
              <OfferTabV3
                id={`tab-${building?.id}`}
                handleClick={offerType => {
                  onOfferChange(offerType)
                  googleAnalyticsEvent({
                    category: 'Precon Floor Plan',
                    action: ['Recent Activity Offer Change', offerType],
                  })
                }}
                tabs={tabs}
                layoutId={`preconFloor-${building?.id}`}
              />
            </_TabContainer>
            <_FilterSortBar>
              <SortBar
                sortType={sortType}
                sortTypeMap={sortTypeMap}
                sortTypeState={sortTypeState}
                setSortTypeState={setSortTypeState}
                pageFrom={pageFrom}
                // googleAnalyticsEvent={googleAnalyticsEvent}
              />
              <FilterBar
                priceFilter={priceFilter}
                priceMetaData={priceMetaData}
                priceChangeHandler={priceChangeHandler}
                sqftFilter={sqftFilter}
                sqftMetaData={sqftMetaData}
                sqftChangeHandler={sqftChangeHandler}
                bedFilter={bedFilter}
                bedTypeList={bedTypeList}
                bedFilterChangeHandler={bedFilterChangeHandler}
                pageFrom={pageFrom}
              />
            </_FilterSortBar>
          </_TopBar>
          {messageData && isNilEmpty(filteredListings) ?
            <_NoListings>
              <div>{messageData.title}</div>
              <div>{messageData.message}</div>
              {messageData.cta &&
              <_FavCTA
                secondary
                small
                aria-label="favourite"
                active={favourited}
                onClick={favBuilding}
              >
                <SvgIcon name={'#icon-favourite'} width={14} height={16} />
                <span>{messageData.cta}</span>
              </_FavCTA>}
            </_NoListings>
            :
            <PreconList listings={filteredListings} />}
          <_LastUpdated>
            <ToolTipV2
              body={disclaimer}
              placement="left"
            />
          </_LastUpdated>
        </_PreconFloorPlanContainer>
      </ShowOnVisible>
    </Collapsible>
  )
}

PreconFloorPlan.propTypes = {
  favourited: PropTypes.bool,
  favBuilding: PropTypes.func,
  collapsibleIdSuffix: PropTypes.string,
  buildingName: PropTypes.string,
  googleAnalyticsEvent: PropTypes.func,
  pageFrom: PropTypes.string,
  building: PropTypes.shape({
    id: PropTypes.number,
    sales_status: PropTypes.string,
    construction_status: PropTypes.string,
    listings: PropTypes.shape({
      precon_for_sale: PropTypes.arrayOf(PropTypes.shape({})),
      precon_sold: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }),

}

export default PreconFloorPlan
